import { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { loadFromLocalStorage, saveToLocalStorage, removeFromLocalStorage } from '../utils/localStorage';
import { debugFilters } from '../utils/debugUtils';

// Create the context
const FilterContext = createContext();

// Create a provider component
export function FilterProvider({ children }) {
  // Key for persisting filters
  const STORAGE_KEY = 'dashboardFilters';
  const hasHydrated = useRef(false);

  const defaultGhTypeCategory = [
    'Annual/Quarterly reports (Strategy, Risks)',
    'Deals',
    'Financial (Segment) performance',
    'Intelligence',
    'Key Developments'
    // Excludes 'Recent Wins'
  ].join(';');

  const defaultFilters = {
    clientName: 'All',
    clientId: '',
    category: defaultGhTypeCategory,
    fiscalYear: 'FY 26',
    subSector: 'All',
    account: 'All',
    accountId: '',
    ghType: 'All',
    ghPriority: ''
  };

  // Hydrate initial filters from localStorage (once) to survive navigation / refresh
  const [filters, setFilters] = useState(() => {
    const stored = loadFromLocalStorage(STORAGE_KEY, null);
    if (stored && typeof stored === 'object') {
      return { ...defaultFilters, ...stored }; // merge to allow future defaults
    }
    return defaultFilters;
  });

  // Persist filters whenever they change (skip first render if already hydrated)
  useEffect(() => {
    if (hasHydrated.current) {
      saveToLocalStorage(STORAGE_KEY, filters);
    } else {
      hasHydrated.current = true; // mark hydration complete
    }
  }, [filters]);

  // Get all growth hacks (already cached in localStorage elsewhere)
  const allGrowthHacks = loadFromLocalStorage('growthHacksData', []);
  
  // Get unique client names from growth hacks
  const [clientNames, setClientNames] = useState(['All']);
  const [categories, setCategories] = useState(['All']);
  
  // Filtered growth hacks
  const [filteredGrowthHacks, setFilteredGrowthHacks] = useState(allGrowthHacks);
  const [filtersResetCount, setFiltersResetCount] = useState(0);
  
  // Apply filters to growth hacks - memoized to prevent unnecessary re-renders
  const applyFilters = useCallback((growthHacks, currentFilters = filters) => {
    // Since we're now doing server-side filtering, we don't need to apply additional client-side filters
    // Just return all the growth hacks as they are already filtered by the server
    let filtered = [...growthHacks];
    
    setFilteredGrowthHacks(filtered);
  }, [filters]);
  
  // Load initial data
  useEffect(() => {
    const growthHacks = loadFromLocalStorage('growthHacksData', []);
    
    // Extract unique client names
    const uniqueClients = ['All', ...new Set(growthHacks.map(hack => hack.Client))];
    setClientNames(uniqueClients);
    
    // Extract unique categories
    const uniqueCategories = ['All', ...new Set(growthHacks.map(hack => hack.Tags))];
    setCategories(uniqueCategories);
    
    // Apply filters
    applyFilters(growthHacks);
  }, [applyFilters]);
  
  // Set up periodic check for changes in local storage
  useEffect(() => {
    const checkForChanges = () => {
      const currentGrowthHacks = loadFromLocalStorage('growthHacksData', []);
      
      // Update unique clients and categories
      const newUniqueClients = ['All', ...new Set(currentGrowthHacks.map(hack => hack.Client))];
      if (JSON.stringify(newUniqueClients) !== JSON.stringify(clientNames)) {
        setClientNames(newUniqueClients);
      }
      
      const newUniqueCategories = ['All', ...new Set(currentGrowthHacks.map(hack => hack.Tags))];
      if (JSON.stringify(newUniqueCategories) !== JSON.stringify(categories)) {
        setCategories(newUniqueCategories);
      }
      applyFilters(currentGrowthHacks, filters);
    };
    
    const intervalId = setInterval(checkForChanges, 500);
    return () => clearInterval(intervalId);
  }, [clientNames, categories, filters, applyFilters]);
  
  // Update filters
  const updateFilters = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    
    // Apply filters to growth hacks
    const growthHacks = loadFromLocalStorage('growthHacksData', []);
    applyFilters(growthHacks, updatedFilters);
    
    // Debug output
    debugFilters(updatedFilters, clientNames, filteredGrowthHacks);
  };
  
  // Reset all filters
  const resetFilters = () => {
    setFilters(defaultFilters);
    removeFromLocalStorage(STORAGE_KEY);
    // Reset to all growth hacks
    const growthHacks = loadFromLocalStorage('growthHacksData', []);
    setFilteredGrowthHacks(growthHacks);
    setFiltersResetCount((count) => count + 1);
  };

  // On page/tab close or full refresh, resetting filters so a new session starts clean
  useEffect(() => {
    const handleBeforeUnload = () => {
      removeFromLocalStorage(STORAGE_KEY);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  
  // The value to be provided to consumers
  const contextValue = {
    filters,
    updateFilters,
    resetFilters,
    clientNames,
    categories,
    filteredGrowthHacks,
    filtersResetCount
  };
  
  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
}

// Custom hook for accessing the filter context
export function useFilters() {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}

export default FilterContext;
